import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	SimplifiedSignatureEditor,
} from "@dune/ui";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useUpdateUserSignature } from "../api/mutations/user.mutations";
import { useMe } from "../api/queries/users.queries";
import { useImageUpload } from "../hooks/useImageUpload";

interface SignatureFormData {
	signature: string;
}

interface SignatureEditorDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export default function SignatureEditorDialog({
	open,
	onOpenChange,
}: SignatureEditorDialogProps) {
	const { me } = useMe();
	const signature = me?.emailSignature || "";

	const updateSignatureMutation = useUpdateUserSignature();
	const { uploadImage } = useImageUpload();

	const form = useForm<SignatureFormData>({
		defaultValues: {
			signature: signature,
		},
	});

	const {
		watch,
		setValue,
		formState: { isDirty },
		reset,
	} = form;
	const currentSignature = watch("signature");

	const duneLogoUrl =
		import.meta.env.VITE_DUNE_LOGO_URL ||
		"https://staging-dune-files-bucket.s3.eu-west-3.amazonaws.com/logo-dune.png";

	const handleSignatureUpdate = (newSignature: string): void => {
		setValue("signature", newSignature, { shouldDirty: true });
	};

	const handleSave = async (): Promise<void> => {
		try {
			const isEmpty = currentSignature.trim() === "";
			const signatureToSave = isEmpty ? null : currentSignature;

			await updateSignatureMutation.mutateAsync({
				emailSignature: signatureToSave || undefined,
			});

			reset({ signature: currentSignature });
			onOpenChange(false);
		} catch (error) {
			console.error("Failed to save signature:", error);
		}
	};

	const handleCancel = (): void => {
		reset({ signature: signature });
		onOpenChange(false);
	};

	useEffect(() => {
		if (open) {
			reset({ signature: signature });
		}
	}, [open, signature, reset]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Éditeur de signature</DialogTitle>
					<DialogDescription>
						Créez une signature professionnelle simple avec le logo DUNE.
						Utilisez le bouton "Template DUNE" pour commencer avec le modèle par
						défaut.
					</DialogDescription>
				</DialogHeader>
				<div className="my-4">
					<SimplifiedSignatureEditor
						content={currentSignature}
						onUpdate={handleSignatureUpdate}
						onImageUpload={uploadImage}
						label="Votre signature"
						duneLogoUrl={duneLogoUrl}
					/>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={handleCancel}>
						Annuler
					</Button>
					<Button
						onClick={handleSave}
						disabled={!isDirty || updateSignatureMutation.isPending}
					>
						{updateSignatureMutation.isPending
							? "Enregistrement..."
							: "Enregistrer"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
